import { useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, Star, Loader2, CheckCircle2 } from "lucide-react";

const AUTHED_PREFIXES = ["/dashboard", "/welcome", "/start", "/property", "/properties", "/settings", "/compare", "/admin"];
const HIDE_PREFIXES = ["/capture"];
const MAX = 2000;

function useWidgetVisible() {
  const { pathname } = useLocation();
  if (HIDE_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"))) return false;
  if (pathname === "/") return true;
  return AUTHED_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"));
}

export function FeedbackWidget() {
  const visible = useWidgetVisible();
  const [open, setOpen] = useState(false);
  if (!visible) return null;
  return (
    <>
      <button
        data-testid="feedback-fab"
        onClick={() => setOpen(true)}
        className="fixed bottom-20 right-5 z-30 md:bottom-5 md:z-40 inline-flex items-center gap-2 rounded-full bg-navy text-white px-4 py-3 shadow-lg hover:bg-navy-hover transition-colors"
        style={{ marginBottom: "env(safe-area-inset-bottom)" }}
      >
        <MessageSquare size={18} />
        <span className="text-sm font-semibold">Feedback</span>
      </button>
      <FeedbackWidgetModal open={open} onOpenChange={setOpen} />
    </>
  );
}

function FeedbackWidgetModal({ open, onOpenChange }) {
  const { user } = useAuth();
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | done | error | rate_limited
  const canSubmit = (rating > 0 || comment.trim().length > 0) && state !== "sending";

  const reset = () => { setRating(0); setHover(0); setComment(""); setEmail(""); setState("idle"); };
  const close = (o) => { if (!o) reset(); onOpenChange(o); };

  const submit = async () => {
    setState("sending");
    try {
      await api.post("/feedback", {
        rating: rating || null,
        comment: comment.trim() || null,
        page_path: window.location.pathname,
        email: !user && email.trim() ? email.trim() : null,
      });
      setState("done");
      setTimeout(() => close(false), 2000);
    } catch (e) {
      if (e?.response?.status === 429) setState("rate_limited");
      else setState("error");
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-md" data-testid="feedback-widget-modal">
        {state === "done" ? (
          <div className="py-6 text-center" data-testid="feedback-widget-thanks">
            <CheckCircle2 size={40} className="mx-auto text-[#16A34A]" />
            <p className="mt-3 text-base font-semibold text-navy">Thanks — this genuinely shapes what gets built next.</p>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-navy">Share feedback</DialogTitle>
            </DialogHeader>
            <div className="flex items-center gap-1" data-testid="feedback-widget-stars">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" data-testid={`feedback-widget-star-${n}`}
                  onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
                  onClick={() => setRating(n === rating ? 0 : n)} className="p-0.5">
                  <Star size={28} className={(hover || rating) >= n ? "fill-amber text-amber" : "text-[#CBD5E1]"} />
                </button>
              ))}
            </div>
            <div>
              <Textarea data-testid="feedback-widget-comment" value={comment} maxLength={MAX}
                onChange={(e) => setComment(e.target.value)}
                placeholder="What's working? What's broken?"
                className="min-h-[96px] rounded-xl bg-white text-sm" />
              <p className="mt-1 text-right text-xs text-[#94A3B8]" data-testid="feedback-widget-counter">{comment.length}/{MAX}</p>
            </div>
            {!user && (
              <Input data-testid="feedback-widget-email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email (if you'd like a reply)" className="rounded-xl" />
            )}
            {state === "rate_limited" && (
              <p data-testid="feedback-widget-ratelimit" className="text-sm text-[#B45309]">You've sent a lot of feedback recently — try again in a bit.</p>
            )}
            {state === "error" && (
              <p data-testid="feedback-widget-error" className="text-sm text-[#B91C1C]">Couldn't send — try again.</p>
            )}
            <Button data-testid="feedback-widget-submit" onClick={submit} disabled={!canSubmit}
              className="w-full h-11 rounded-xl bg-navy hover:bg-navy-hover text-white font-semibold disabled:opacity-50">
              {state === "sending" ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
              Send feedback
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
